// ============================================
// chatService.js - Chat Session & Message Service
// ============================================
// Handles all chat-related operations:
// - Session CRUD
// - Message sending/receiving
// - Local state sync with backend

import api from './api';

// ============================================
// SESSION MANAGEMENT
// ============================================

/**
 * Load all sessions from backend
 * @returns {Promise<{sessions: Array, error?: string}>}
 */
export async function loadSessions() {
  const result = await api.getSessions();

  if (result.error) {
    return { sessions: [], error: result.error };
  }

  // Transform backend sessions to frontend format
  const sessions = (result.data?.sessions || []).map(transformSession);

  return { sessions };
}

/**
 * Load a single session with all messages
 * @param {string} sessionId - UUID of session
 * @returns {Promise<{session: Object, messages: Array, error?: string}>}
 */
export async function loadSession(sessionId) {
  const result = await api.getSession(sessionId);

  if (result.error) {
    return { session: null, messages: [], error: result.error };
  }

  const session = transformSession(result.data);
  const messages = (result.data?.messages || []).map(transformMessage);

  return { session, messages };
}

/**
 * Create a new session (happens automatically on first message)
 * This is mainly for pre-creating empty sessions if needed
 */
export async function createSession() {
  // Backend creates session on first message
  // Return a local placeholder
  return {
    session: {
      id: `temp-${Date.now()}`,
      title: 'New conversation',
      preview: 'Start a new conversation...',
      messages: [],
      isPinned: false,
      isArchived: false,
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString(),
    },
  };
}

/**
 * Update session properties
 * @param {string} sessionId
 * @param {Object} updates - {title?, system_prompt?, is_active?}
 */
export async function updateSession(sessionId, updates) {
  const result = await api.updateSession(sessionId, updates);

  if (result.error) {
    return { error: result.error };
  }

  return { session: transformSession(result.data) };
}

/**
 * Delete (archive) a session
 * @param {string} sessionId
 */
export async function deleteSession(sessionId) {
  const result = await api.deleteSession(sessionId);
  return { success: !result.error, error: result.error };
}

// ============================================
// MESSAGE MANAGEMENT
// ============================================

/**
 * Send a message and get AI response
 * @param {string} content - Message content
 * @param {string|null} sessionId - Existing session ID or null for new
 * @returns {Promise<{sessionId: string, userMessage: Object, assistantMessage: Object, remaining: number, error?: string}>}
 */
export async function sendMessage(content, sessionId = null) {
  const result = await api.sendMessage(content, sessionId);

  if (result.error) {
    return { error: result.error, status: result.status };
  }

  return {
    sessionId: result.data.session_id,
    userMessage: transformMessage(result.data.user_message),
    assistantMessage: transformMessage(result.data.assistant_message),
    remaining: result.data.remaining,
    dailyLimit: result.data.daily_limit,
    authenticated: result.data.authenticated,
  };
}

// ============================================
// USAGE & LIMITS
// ============================================

/**
 * Get current usage status
 * @returns {Promise<{usage: Object, error?: string}>}
 */
export async function getUsageStatus() {
  const result = await api.getUsageStatus();

  if (result.error) {
    return { usage: null, error: result.error };
  }

  return {
    usage: {
      authenticated: result.data.authenticated,
      tier: result.data.tier,
      remaining: result.data.remaining,
      dailyLimit: result.data.daily_limit,
      messagesToday: result.data.messages_today,
      messagesTotal: result.data.messages_total,
      sessionsCount: result.data.sessions_count,
      deviceId: result.data.device_id,
    },
  };
}

/**
 * Initialize device (register fingerprint)
 */
export async function initializeDevice() {
  const result = await api.registerDevice();

  if (result.error) {
    console.warn('Device registration failed:', result.error);
    return { success: false, error: result.error };
  }

  return {
    success: true,
    fingerprintHash: result.data.fingerprint_hash,
    isNew: result.data.is_new,
    remaining: result.data.remaining,
  };
}

/**
 * Link device sessions to authenticated user
 */
export async function linkDeviceToUser() {
  const result = await api.linkDevice();

  if (result.error) {
    return { success: false, error: result.error };
  }

  return {
    success: true,
    sessionsTransferred: result.data.sessions_transferred,
  };
}

// ============================================
// TRANSFORM FUNCTIONS
// ============================================

/**
 * Transform backend session to frontend format
 */
function transformSession(backendSession) {
  if (!backendSession) return null;

  return {
    id: backendSession.id,
    title: backendSession.title || 'New conversation',
    preview: backendSession.preview?.content || '',
    messages: [], // Messages loaded separately
    isPinned: false, // TODO: Add to backend if needed
    isArchived: !backendSession.is_active,
    messageCount: backendSession.message_count || 0,
    createdAt: backendSession.created_at,
    updatedAt: backendSession.updated_at,
    lastMessageAt: backendSession.last_message_at,
  };
}

/**
 * Transform backend message to frontend format
 */
function transformMessage(backendMessage) {
  if (!backendMessage) return null;

  return {
    id: backendMessage.id,
    role: backendMessage.role,
    content: backendMessage.content,
    timestamp: formatTime(backendMessage.created_at),
    createdAt: backendMessage.created_at,
    tokensUsed: backendMessage.tokens_used,
    modelUsed: backendMessage.model_used,
    isError: backendMessage.is_error,
  };
}

/**
 * Format timestamp for display
 */
function formatTime(isoString) {
  if (!isoString) return '';
  const date = new Date(isoString);
  return date.toLocaleTimeString([], {
    hour: '2-digit',
    minute: '2-digit',
  });
}

// ============================================
// EXPORTS
// ============================================

export default {
  loadSessions,
  loadSession,
  createSession,
  updateSession,
  deleteSession,
  sendMessage,
  getUsageStatus,
  initializeDevice,
  linkDeviceToUser,
};
